import { Link, useNavigate } from "react-router-dom";
import { Star, MapPin, Heart, Sparkles } from "lucide-react";
import { useCurrency } from "@/lib/currency";
import { useFavorites } from "@/lib/favorites";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";

export default function ProductCard({ p, kind = "product" }) {
  const { format, getDisplayPrice } = useCurrency();
  const { isFav, toggle } = useFavorites();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const link = kind === "product" ? `/produit/${p.id}` : `/service/${p.id}`;
  const displayPrice = getDisplayPrice(p);
  const favorited = isFav(kind, p.id);
  const displayTitle = (lang === "en" && p.title_en) ? p.title_en : p.title;
  const [featured, setFeatured] = useState(!!p.featured_as_new);

  const onHeartClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await toggle(kind, p.id);
    if (res.needLogin) {
      toast.info(t("fav.login_needed"));
      nav("/connexion");
      return;
    }
    toast.success(res.added ? t("fav.added") : t("fav.removed"));
  };

  const onPinClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const r = await api.patch(`/admin/${kind === "product" ? "products" : "services"}/${p.id}/featured`);
      setFeatured(r.data.featured_as_new);
      toast.success(r.data.featured_as_new ? "✨ Épinglé en Nouveautés" : "Retiré des Nouveautés");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <Link to={link} className="product-card block group relative" data-testid={`${kind}-card-${p.id}`}>
      <button
        onClick={onHeartClick}
        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all ${favorited ? "bg-white shadow-lg" : "bg-white/80 hover:bg-white hover:shadow-md"}`}
        aria-label={favorited ? "Retirer des favoris" : "Ajouter aux favoris"}
        data-testid={`fav-btn-${kind}-${p.id}`}
      >
        <Heart className={`w-4.5 h-4.5 transition-all ${favorited ? "fill-[#C85A32] text-[#C85A32]" : "text-[#4B5563]"}`} strokeWidth={2} />
      </button>
      {user?.role === "admin" && (
        <button
          onClick={onPinClick}
          className={`absolute top-3 left-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all ${featured ? "bg-[#C85A32] text-white shadow-lg" : "bg-white/80 hover:bg-white text-[#4B5563] hover:text-[#C85A32]"}`}
          aria-label={featured ? "Retirer des Nouveautés" : "Épingler en Nouveautés"}
          title={featured ? "Retirer des Nouveautés" : "Épingler en Nouveautés"}
          data-testid={`pin-btn-${kind}-${p.id}`}
        >
          <Sparkles className="w-4 h-4" strokeWidth={2} />
        </button>
      )}
      <div className="aspect-[4/5] overflow-hidden bg-[#F5F5F0] rounded-lg">
        {p.images?.[0] ? (
          <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#4B5563] text-sm">Sans image</div>
        )}
      </div>
      <div className="pt-4">
        <h3 className="heading font-bold text-[15px] leading-tight text-[#111812] line-clamp-2 group-hover:text-[#1A3322]">
          {p.title}
        </h3>
        <div className="flex items-center gap-2 mt-2 text-xs text-[#4B5563]">
          <MapPin className="w-3 h-3" />
          <span className="truncate">{p.location || p.zone || "Sur devis"}</span>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="heading font-extrabold text-lg text-[#1A3322]" data-testid={`${kind}-price-${p.id}`}>
            {format(displayPrice)}
          </span>
          <div className="flex items-center gap-1 text-xs">
            <Star className="w-3.5 h-3.5 fill-[#D98A2C] text-[#D98A2C]" />
            <span className="font-medium">{p.rating_avg > 0 ? p.rating_avg.toFixed(1) : "—"}</span>
            <span className="text-[#4B5563]">({p.rating_count || 0})</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
